import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { BioButton } from "@/components/bio/BioButton";
import { Input } from "@/components/ui/input";
import { StatusBadge } from "@/components/bio/StatusBadge";
import {
    CreditCard,
    Landmark,
    Banknote,
    Check,
    X,
    Clock,
    ArrowLeft,
    Wallet,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatUSD } from "@/lib/format";

const QUICK = [5, 10, 20, 50];
const RAPIDPAY_LOGO = "https://customer-assets-cm19k8pv.emergentagent.net/job_biofood-escolar/artifacts/eff7d4ee3da001ef_White-color.png";
const YAPPY_LOGO = "https://customer-assets-cm19k8pv.emergentagent.net/job_biofood-escolar/artifacts/989446679fb16998_Yappy%20Logo%20Website.png";
const METHODS = [
    { id: "rapidpay", label: "RapidPay", logo: RAPIDPAY_LOGO, desc: "Pago con RapidPay", dark: true },
    { id: "yappy", label: "Yappy", logo: YAPPY_LOGO, desc: "Pago móvil Yappy", dark: false },
];

export function RechargeFlow({ open, onOpenChange, student, onSuccess }) {
    const [step, setStep] = useState(1);
    const [amount, setAmount] = useState(10);
    const [method, setMethod] = useState("rapidpay");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);

    const reset = () => {
        setStep(1);
        setAmount(10);
        setMethod("card");
        setResult(null);
        setLoading(false);
    };

    const close = (v) => {
        onOpenChange(v);
        if (!v) setTimeout(reset, 250);
    };

    const pay = async () => {
        setLoading(true);
        try {
            const { data } = await api.post("/parent/recharge", {
                student_id: student.id,
                amount: Number(amount),
                method,
            });
            setResult(data);
            setStep(3);
            if (data.result.status === "confirmed") {
                toast.success("Recarga confirmada", { description: `${formatUSD(amount)} acreditados (MODO DEMO).` });
                onSuccess?.(data.new_balance);
            } else if (data.result.status === "pending") {
                toast.info("Recarga pendiente", { description: "Se confirmará al pagar en cafetería." });
            }
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    const statusView = () => {
        const s = result?.result?.status;
        const map = {
            confirmed: { icon: Check, color: "#0F807B", bg: "#E8F9F8", title: "¡Recarga confirmada!" },
            pending: { icon: Clock, color: "#B36B00", bg: "#FEF6E9", title: "Recarga pendiente" },
            rejected: { icon: X, color: "#B71C1C", bg: "#FDECEA", title: "Recarga rechazada" },
        };
        const v = map[s] || map.rejected;
        return (
            <div className="text-center py-2">
                <div className="mx-auto h-16 w-16 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: v.bg, color: v.color }}>
                    <v.icon className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-bold text-bio-text">{v.title}</h3>
                <div className="mt-2 flex justify-center"><StatusBadge status={s} /></div>
                <div className="mt-5 bg-bio-bg rounded-bio-card p-4 text-left space-y-2 text-sm">
                    <Row k="Monto" v={formatUSD(result.transaction.amount)} />
                    <Row k="Referencia" v={result.result.reference} />
                    <Row k="Nuevo saldo" v={formatUSD(result.new_balance)} bold />
                    <p className="text-xs text-bio-text-muted pt-1">{result.result.message}</p>
                </div>
                <BioButton className="w-full mt-5" data-testid="recharge-done" onClick={() => close(false)}>Listo</BioButton>
            </div>
        );
    };

    return (
        <Dialog open={open} onOpenChange={close}>
            <DialogContent className="rounded-bio-lg max-w-md border-bio-border-subtle p-6">
                <DialogHeader>
                    <span className="inline-flex w-fit items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-[#FEF6E9] text-[#B36B00] mb-1">
                        MODO DEMO · RapidPay · Yappy
                    </span>
                    <DialogTitle className="text-xl font-bold text-bio-text flex items-center gap-2">
                        <Wallet className="h-5 w-5 text-bio-primary" /> Recargar saldo
                    </DialogTitle>
                    <DialogDescription className="text-sm text-bio-text-secondary">
                        {student
                            ? `${student.name} · Saldo actual ${formatUSD(student.balance)}`
                            : "Recarga el monedero del estudiante."}
                    </DialogDescription>
                </DialogHeader>

                {/* Stepper */}
                {step < 3 && (
                    <div className="flex items-center gap-2 py-2">
                        {[1, 2].map((n) => (
                            <React.Fragment key={n}>
                                <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold ${step >= n ? "bg-bio-primary text-white" : "bg-bio-bg-alt text-bio-text-muted"}`}>{n}</div>
                                {n === 1 && <div className={`flex-1 h-0.5 ${step > 1 ? "bg-bio-primary" : "bg-bio-bg-alt"}`} />}
                            </React.Fragment>
                        ))}
                        <span className="text-xs text-bio-text-muted ml-2">{step === 1 ? "Monto" : "Método de pago"}</span>
                    </div>
                )}

                <AnimatePresence mode="wait">
                    {step === 1 && (
                        <motion.div key="s1" initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} transition={{ duration: 0.2 }}>
                            <div className="grid grid-cols-4 gap-2 py-2">
                                {QUICK.map((v) => (
                                    <button key={v} data-testid={`quick-${v}`} onClick={() => setAmount(v)} className={`h-14 rounded-bio border-[1.5px] font-bold transition-colors ${amount === v ? "border-bio-accent bg-[#EEF7FC] text-bio-primary" : "border-bio-border text-bio-text hover:border-bio-accent"}`}>
                                        ${v}
                                    </button>
                                ))}
                            </div>
                            <div className="space-y-1.5 mt-2">
                                <label className="text-sm font-semibold text-bio-text">Otro monto (USD)</label>
                                <Input data-testid="recharge-amount" type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-12 rounded-bio bg-bio-bg border-[1.5px] font-semibold text-lg" />
                            </div>
                            <BioButton className="w-full mt-5" data-testid="recharge-next" disabled={!amount || Number(amount) <= 0} onClick={() => setStep(2)}>
                                Continuar
                            </BioButton>
                        </motion.div>
                    )}

                    {step === 2 && (
                        <motion.div key="s2" initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} transition={{ duration: 0.2 }}>
                            <div className="space-y-2 py-2">
                                {METHODS.map((m) => (
                                    <button key={m.id} data-testid={`method-${m.id}`} onClick={() => setMethod(m.id)} className={`w-full flex items-center gap-3 rounded-bio border-[1.5px] p-3.5 text-left transition-colors ${method === m.id ? "border-bio-accent bg-[#EEF7FC]" : "border-bio-border hover:border-bio-accent"}`}>
                                        <div className={`h-10 w-20 rounded-bio border flex items-center justify-center p-1.5 shrink-0 ${m.dark ? "bg-bio-primary-dark border-bio-primary-dark" : "bg-white border-bio-border-subtle"}`}><img src={m.logo} alt={m.label} className="max-h-6 max-w-full object-contain" /></div>
                                        <div className="flex-1">
                                            <p className="font-semibold text-bio-text text-sm">{m.label}</p>
                                            <p className="text-xs text-bio-text-muted">{m.desc}</p>
                                        </div>
                                        <div className={`h-5 w-5 rounded-full border-2 ${method === m.id ? "border-bio-primary bg-bio-primary" : "border-bio-border"} flex items-center justify-center`}>
                                            {method === m.id && <Check className="h-3 w-3 text-white" />}
                                        </div>
                                    </button>
                                ))}
                            </div>
                            <div className="flex gap-2 mt-4">
                                <BioButton variant="ghost" onClick={() => setStep(1)} className="flex-none"><ArrowLeft className="h-4 w-4" /></BioButton>
                                <BioButton className="flex-1" data-testid="recharge-pay" loading={loading} onClick={pay}>
                                    Pagar {formatUSD(amount)}
                                </BioButton>
                            </div>
                        </motion.div>
                    )}

                    {step === 3 && (
                        <motion.div key="s3" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.25 }}>
                            {statusView()}
                        </motion.div>
                    )}
                </AnimatePresence>
            </DialogContent>
        </Dialog>
    );
}

function Row({ k, v, bold }) {
    return (
        <div className="flex items-center justify-between">
            <span className="text-bio-text-secondary">{k}</span>
            <span className={bold ? "font-bold text-bio-text" : "text-bio-text"}>{v}</span>
        </div>
    );
}
